import * as React from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeftRight,
  Receipt,
  Plus,
  Edit,
  Trash2,
  LogIn,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  FileText,
  Pencil,
} from "lucide-react";

import { AppLayout } from "../../components/ui-v2/layout/AppLayout";
import { Card, CardContent } from "../../components/ui-v2/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../../components/ui-v2/table";
import { Badge } from "../../components/ui-v2/badge";
import { Button, RowIconAction } from "../../components/ui-v2/button";
import { Skeleton } from "../../components/ui-v2/skeleton";
import { FilterPill, FilterPillGroup } from "../../components/ui-v2/filter-pill";
import { StatCard } from "../../components/ui-v2/stat-card";
import { Heading, Text, MonoText } from "../../components/ui-v2/typography";
import { CurrencyValue, PercentValue } from "../../components/ui-v2/value-display";
import { AddClosedPositionDialog } from "../../components/ui-v2/AddClosedPositionDialog";
import { EditClosedPositionDialog } from "../../components/ui-v2/EditClosedPositionDialog";
import { DeleteClosedPositionDialog } from "../../components/ui-v2/DeleteClosedPositionDialog";
import { AddDividendDialog } from "../../components/ui-v2/AddDividendDialog";
import { EditDividendDialog } from "../../components/ui-v2/EditDividendDialog";
import { DeleteDividendDialog } from "../../components/ui-v2/DeleteDividendDialog";
import { AddWithdrawalDialog } from "../../components/ui-v2/AddWithdrawalDialog";
import { EditWithdrawalDialog } from "../../components/ui-v2/EditWithdrawalDialog";
import { DeleteWithdrawalDialog } from "../../components/ui-v2/DeleteWithdrawalDialog";
import { EmptyState } from "../../components/ui-v2/empty-state";
import { API_BASE_URL } from '../../lib/api';

/**
 * V2 Transactions Page — Institutional Console redesign (issue #236,
 * DESIGN-SPEC.md §5 "Activity/Realised gains": "KPI strip incl. win-rate bar
 * and best/worst close -> filterable closed-positions table; annual
 * statements = accent-tinted rows + 'Statement' badge").
 *
 * Lives under the shared "Activity" top-level nav section (navRegistry.js
 * `subTabGroups.activity`: Feed/Realised gains/Dividends/Withdrawals/
 * Contributions/Events); AppLayout/PageToolbar renders those sub-tabs from
 * the route. This component supplies the shared "Activity" toolbar title
 * (matching Transactions.dc.html/Contributions.dc.html/Events.dc.html, all
 * of which show "Activity" regardless of the active sub-tab — a deviation
 * from the pre-restyle per-type titles "Realized Gains"/"Dividends"/
 * "Withdrawals"/"Activity Feed", declared and matching the mockup exactly).
 *
 * Four sub-views share the URL param `/transactions/:type`
 * (activity/realized-gains/dividends/withdrawals) exactly as before — no
 * routing change. Only "Realised gains" (type=realized-gains) has a pixel
 * mockup (Transactions.dc.html); Dividends/Withdrawals/Feed apply the same
 * §4 component restyle (StatCard KPIs, restyled Table, RowIconAction) for
 * visual consistency since no dedicated mockup exists for them (declared
 * deviation, same class as CashFlow.jsx's precedent).
 *
 * Declared deviation: the mockup's "Export CSV" toolbar button
 * (Transactions.dc.html:31) is NOT implemented — no backend endpoint scopes
 * a CSV export to closed-positions/realised-gains (the existing
 * `ExportDialog` component exports whole-portfolio data in JSON/Markdown/
 * PDF, not a per-view CSV), and shipping a button with no real handler would
 * be a fake affordance (explicitly prohibited by the review contract). Not
 * a functionality loss — no CSV export existed pre-restyle either.
 *
 * All prior functionality retained across all four sub-views: fetch/refresh,
 * add/edit/delete CRUD dialogs, loading/error/empty states, annual-statement
 * accent-tinted rows, per-row tooltips consolidated into the always-visible
 * RowIconAction. New per mockup (Realised gains only): win-rate KPI with a
 * 2-color progress bar, best/worst-close KPI, Gains/Losses/Annual-statements
 * FilterPillGroup (client-side, additive — no prior equivalent existed).
 */

function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function eur(value) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(value || 0);
}

function signedEur(value) {
  return `${value >= 0 ? "+" : "−"}${eur(Math.abs(value))}`;
}

/**
 * Win-rate KPI card — StatCard has no slot for extra content below the delta
 * line (its `value`/`delta` props render fixed text, not arbitrary nodes),
 * so this mirrors StatCard's exact §4.2 classes (surface/border/r8/p14-16,
 * 11px uppercase micro-label, 22px mono value, 11.5px mono delta) and adds
 * the mockup's 2-color win/loss progress bar underneath (Transactions.dc.html
 * :49-57) — a local composition, not a ui-v2 primitive change.
 */
function WinRateCard({ winRate, gainers, losers }) {
  return (
    <Card className="flex flex-col gap-2.5 rounded-lg py-3.5 px-4">
      <div className="min-w-0">
        <div className="truncate text-[11px] font-semibold uppercase tracking-[0.06em] text-stat-label">
          Win rate
        </div>
        <div className="mt-[5px] flex items-baseline gap-2">
          <span className="font-mono text-[22px] font-semibold leading-none tabular-nums text-foreground">
            {winRate.toFixed(1)}%
          </span>
          <span className="font-mono text-xs tabular-nums text-text-faint">
            {gainers}W · {losers}L
          </span>
        </div>
      </div>
      <div
        className="flex h-1 gap-0.5 overflow-hidden rounded-full"
        role="img"
        aria-label={`Win rate ${winRate.toFixed(1)}%, ${gainers} wins, ${losers} losses`}
      >
        <div className="bg-success" style={{ width: `${winRate}%` }} />
        <div className="flex-1 bg-destructive opacity-60" />
      </div>
    </Card>
  );
}

/**
 * Shared 4-up KPI strip skeleton + toolbar/table skeleton, single-cycle
 * Skeleton primitive (§6/§7: no shimmer >1 cycle, motion-reduce disables it).
 */
function LoadingState({ columns = 5 }) {
  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={`stat-skeleton-${i}`} className="py-3.5 px-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-2 h-6 w-28" />
          </Card>
        ))}
      </div>
      <Card>
        <CardContent className="p-0">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={`row-skeleton-${i}`} className="flex items-center gap-4 border-b border-hairline px-[18px] py-2">
              {Array.from({ length: columns }, (_, c) => (
                <Skeleton key={c} className={c === 0 ? "h-4 w-20" : "h-4 w-16 flex-1"} />
              ))}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function ErrorCard({ title, error, onRetry }) {
  return (
    <Card>
      <CardContent className="py-16">
        <div className="text-center">
          <Heading level={4} className="text-destructive">
            {title}
          </Heading>
          <Text variant="secondary" className="mt-2">
            {error}
          </Text>
          <Button variant="outline" className="mt-4" onClick={onRetry}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Shared table card header: title + mono count + right-aligned toolbar
 * controls (FilterPillGroup and/or Refresh/Add buttons), matching
 * Transactions.dc.html:68-77 / Contributions.dc.html:64-73.
 */
function TableCardHeader({ title, count, children }) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-hairline px-[18px] py-2.5">
      <Heading level={4} className="text-[13.5px] font-semibold">
        {title}
      </Heading>
      {count !== undefined && (
        <MonoText size="xs" className="text-text-faint">
          {count}
        </MonoText>
      )}
      <div className="ml-auto flex items-center gap-2">{children}</div>
    </div>
  );
}

// ============================================
// Activity Feed Component
// ============================================
function ActivityFeed({ registerToolbarActions }) {
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const fetchLogs = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/logs`, {
        params: { limit: 50 },
        withCredentials: true,
      });
      setLogs(response.data.logs || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching logs:", err);
      setError(err.message);
    }
  }, []);

  React.useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchLogs();
      setLoading(false);
    };
    loadInitialData();
  }, [fetchLogs]);

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    await fetchLogs();
    setIsRefreshing(false);
  }, [fetchLogs]);

  React.useEffect(() => {
    registerToolbarActions({ onRefresh: handleRefresh, isRefreshing });
  }, [registerToolbarActions, handleRefresh, isRefreshing]);

  // Filter to holding-related events for activity display
  const holdingEvents = React.useMemo(() => {
    return logs.filter((log) =>
      ["HOLDING_CREATED", "HOLDING_UPDATED", "HOLDING_DELETED"].includes(
        log.eventType
      )
    );
  }, [logs]);

  // Calculate summary stats
  const summary = React.useMemo(() => {
    const created = logs.filter(l => l.eventType === "HOLDING_CREATED").length;
    const updated = logs.filter(l => l.eventType === "HOLDING_UPDATED").length;
    const deleted = logs.filter(l => l.eventType === "HOLDING_DELETED").length;
    return { created, updated, deleted, total: holdingEvents.length };
  }, [logs, holdingEvents]);

  const eventTypeConfig = {
    HOLDING_CREATED: { icon: Plus, label: "Added", variant: "success" },
    HOLDING_UPDATED: { icon: Edit, label: "Updated", variant: "info" },
    HOLDING_DELETED: { icon: Trash2, label: "Deleted", variant: "destructive" },
    LOGIN: { icon: LogIn, label: "Login", variant: "secondary" },
    LOGOUT: { icon: LogIn, label: "Logout", variant: "secondary" },
    PORTFOLIO_REFRESHED: { icon: RefreshCw, label: "Refreshed", variant: "secondary" },
    SNAPSHOT_CREATED: { icon: FileText, label: "Snapshot", variant: "info" },
    CONTRIBUTION_ADDED: { icon: TrendingUp, label: "Contribution", variant: "success" },
    WITHDRAWAL_ADDED: { icon: TrendingDown, label: "Withdrawal", variant: "warning" },
  };

  if (loading) return <LoadingState columns={4} />;

  if (error) {
    return <ErrorCard title="Failed to load activity" error={error} onRetry={handleRefresh} />;
  }

  if (holdingEvents.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={ArrowLeftRight}
          title="No Recent Activity"
          description="Activity will appear here when you add, edit, or delete holdings from your portfolio."
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total events" value={String(summary.total)} delta="Recent activity" />
        <StatCard label="Holdings added" value={String(summary.created)} delta="New positions" valueTone="positive" />
        <StatCard label="Holdings updated" value={String(summary.updated)} delta="Modifications" />
        <StatCard label="Holdings deleted" value={String(summary.deleted)} delta="Removed" valueTone={summary.deleted > 0 ? "negative" : "default"} />
      </div>

      <Card className="overflow-hidden p-0">
        <TableCardHeader title="Recent activity" count={`${holdingEvents.length} events`} />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-[18px]">Time</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead className="px-[18px] text-right">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {holdingEvents.slice(0, 15).map((log) => {
              const config = eventTypeConfig[log.eventType] || {
                icon: ArrowLeftRight,
                label: log.eventType,
                variant: "secondary",
              };
              const Icon = config.icon;
              const ticker = log.metadata?.ticker || "-";
              const assetName = log.metadata?.assetName || log.metadata?.name || "";
              const units = log.metadata?.units;
              const value = log.metadata?.totalValue || log.metadata?.value;

              return (
                <TableRow key={log.logId || log.timestamp}>
                  <TableCell className="px-[18px] font-mono text-[11px] text-text-faint">
                    {formatRelativeTime(log.timestamp)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <Badge variant={config.variant} size="sm">
                        {config.label}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <MonoText size="sm" className="font-medium">
                        {ticker}
                      </MonoText>
                      {assetName && ticker !== assetName && (
                        <Text size="xs" className="text-muted-foreground truncate max-w-[150px]" title={assetName}>
                          {assetName}
                        </Text>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="px-[18px] text-right">
                    <div className="flex flex-col items-end">
                      {units && (
                        <MonoText size="xs" className="text-muted-foreground">
                          {parseFloat(units).toLocaleString(undefined, { maximumFractionDigits: 4 })} units
                        </MonoText>
                      )}
                      {value && (
                        <MonoText size="xs" className="text-foreground">
                          <CurrencyValue value={value} size="xs" />
                        </MonoText>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============================================
// Realized Gains Component
// ============================================
const POSITION_FILTERS = [
  { value: "all", label: "All" },
  { value: "gains", label: "Gains" },
  { value: "losses", label: "Losses" },
  { value: "annual", label: "Annual statements" },
];

function getPositionGain(position) {
  const isAnnual = position.statementType === "ANNUAL";
  let gain;
  let gainCurrency;

  if (isAnnual) {
    gain = parseFloat(position.realizedGainEUR) || 0;
    gainCurrency = "EUR";
  } else if (position.realizedGainEUR !== undefined && position.realizedGainEUR !== null) {
    gain = parseFloat(position.realizedGainEUR);
    gainCurrency = "EUR";
  } else {
    const saleProceeds = (parseFloat(position.quantitySold) || 0) * (parseFloat(position.salePrice) || 0);
    const costBasis = (parseFloat(position.quantitySold) || 0) * (parseFloat(position.purchasePrice) || 0);
    gain = saleProceeds - costBasis;
    gainCurrency = position.currency || "EUR";
  }

  let gainPercent = null;
  if (!isAnnual) {
    const costBasis = (parseFloat(position.quantitySold) || 0) * (parseFloat(position.purchasePrice) || 0);
    gainPercent = costBasis > 0 ? (gain / costBasis) * 100 : 0;
  }

  return { isAnnual, gain, gainCurrency, gainPercent };
}

function RealizedGains({ registerToolbarActions }) {
  const [positions, setPositions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [positionFilter, setPositionFilter] = React.useState("all");

  // CRUD Dialog States
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedPosition, setSelectedPosition] = React.useState(null);

  const fetchPositions = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/transactions/closed-positions`,
        { withCredentials: true }
      );
      setPositions(response.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching closed positions:", err);
      setError(err.message);
    }
  }, []);

  React.useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchPositions();
      setLoading(false);
    };
    loadInitialData();
  }, [fetchPositions]);

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    await fetchPositions();
    setIsRefreshing(false);
  }, [fetchPositions]);

  const handleAddSuccess = () => fetchPositions();

  const handleEditClick = (position) => {
    setSelectedPosition(position);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    fetchPositions();
    setSelectedPosition(null);
  };

  const handleDeleteClick = (position) => {
    setSelectedPosition(position);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSuccess = () => {
    fetchPositions();
    setSelectedPosition(null);
  };

  const handleAddPosition = React.useCallback(() => setAddDialogOpen(true), []);

  React.useEffect(() => {
    registerToolbarActions({ onRefresh: handleRefresh, isRefreshing, onAdd: handleAddPosition, addLabel: "Record sale" });
  }, [registerToolbarActions, handleRefresh, isRefreshing, handleAddPosition]);

  // Calculate summary
  const summary = React.useMemo(() => {
    let totalGain = 0;
    let gainers = 0;
    let losers = 0;
    let best = null;
    let worst = null;

    positions.forEach((p) => {
      const { isAnnual, gain, gainPercent } = getPositionGain(p);
      totalGain += gain;
      if (gain > 0) gainers += 1;
      if (gain < 0) losers += 1;

      if (!isAnnual) {
        const candidate = { ticker: p.ticker, gain, gainPercent };
        if (!best || gain > best.gain) best = candidate;
        if (!worst || gain < worst.gain) worst = candidate;
      }
    });

    return {
      totalGain,
      positionCount: positions.length,
      gainers,
      losers,
      winRate: gainers + losers > 0 ? (gainers / (gainers + losers)) * 100 : 0,
      best,
      worst,
    };
  }, [positions]);

  const visiblePositions = React.useMemo(() => {
    if (positionFilter === "all") return positions;
    return positions.filter((p) => {
      const { isAnnual, gain } = getPositionGain(p);
      if (positionFilter === "annual") return isAnnual;
      if (positionFilter === "gains") return !isAnnual && gain > 0;
      if (positionFilter === "losses") return !isAnnual && gain < 0;
      return true;
    });
  }, [positions, positionFilter]);

  if (loading) return <LoadingState columns={6} />;

  if (error) {
    return <ErrorCard title="Failed to load realized gains" error={error} onRetry={handleRefresh} />;
  }

  if (positions.length === 0) {
    return (
      <>
        <Card>
          <EmptyState
            icon={Receipt}
            title="No Closed Positions"
            description="When you sell holdings, your realized gains and losses will be tracked here for tax reporting purposes."
            primaryAction={{ label: "Add Closed Position", onClick: () => setAddDialogOpen(true) }}
          />
        </Card>
        <AddClosedPositionDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} onSuccess={handleAddSuccess} />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      {/* KPI strip (Transactions.dc.html:37-66) */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total realised gain"
          value={signedEur(summary.totalGain)}
          valueTone={summary.totalGain >= 0 ? "positive" : "negative"}
          delta="net of losses"
        />
        <StatCard label="Closed positions" value={String(summary.positionCount)} delta="since inception" />
        <WinRateCard winRate={summary.winRate} gainers={summary.gainers} losers={summary.losers} />
        <StatCard
          label="Best / worst close"
          value={
            <div className="flex flex-col gap-0.5 text-xs font-normal normal-case tracking-normal">
              {summary.best && (
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-text-secondary">{summary.best.ticker}</span>
                  <span className="font-mono text-success">{signedEur(summary.best.gain)}</span>
                </div>
              )}
              {summary.worst && (
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-text-secondary">{summary.worst.ticker}</span>
                  <span className="font-mono text-destructive">{signedEur(summary.worst.gain)}</span>
                </div>
              )}
            </div>
          }
        />
      </div>

      <Card className="overflow-hidden p-0">
        <TableCardHeader title="Closed positions" count={`${visiblePositions.length} records`}>
          <FilterPillGroup>
            {POSITION_FILTERS.map((filter) => (
              <FilterPill key={filter.value} active={positionFilter === filter.value} onClick={() => setPositionFilter(filter.value)}>
                {filter.label}
              </FilterPill>
            ))}
          </FilterPillGroup>
        </TableCardHeader>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-[18px]">Date</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Buy</TableHead>
              <TableHead className="text-right">Sell</TableHead>
              <TableHead className="text-right">Gain / loss</TableHead>
              <TableHead className="px-[18px] text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visiblePositions.slice(0, 15).map((position) => {
              const { isAnnual, gain, gainCurrency, gainPercent } = getPositionGain(position);

              return (
                <TableRow key={position.transactionId} className={isAnnual ? "bg-accent-tint/40" : undefined}>
                  <TableCell className="px-[18px] font-mono text-[11.5px] text-muted-token">
                    {formatDate(position.date || position.saleDate)}
                  </TableCell>
                  <TableCell>
                    {isAnnual ? (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Annual Statement</span>
                        <Badge variant="info" size="sm">Statement</Badge>
                      </div>
                    ) : (
                      <span className="whitespace-nowrap">
                        <span className="font-medium">{position.assetName || position.ticker}</span>
                        {position.ticker && position.ticker !== position.assetName && (
                          <MonoText size="xs" className="ml-1.5 text-text-faint">{position.ticker}</MonoText>
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-token">{position.platform || "-"}</TableCell>
                  <TableCell className="text-right font-mono text-muted-token">
                    {isAnnual ? "-" : position.quantitySold ? parseFloat(position.quantitySold).toLocaleString(undefined, { maximumFractionDigits: 4 }) : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    {isAnnual ? <span className="font-mono text-text-faint">-</span> : (
                      <CurrencyValue value={parseFloat(position.purchasePrice) || 0} currency={position.currency} size="sm" className="font-mono" />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {isAnnual ? <span className="font-mono text-text-faint">-</span> : (
                      <CurrencyValue value={parseFloat(position.salePrice) || 0} currency={position.currency} size="sm" className="font-mono" />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <CurrencyValue value={gain} currency={gainCurrency} size="sm" colorize showSign className="font-mono" />
                      {gainPercent !== null && <PercentValue value={gainPercent} size="xs" showTrend className="opacity-85" />}
                    </div>
                  </TableCell>
                  <TableCell className="px-[18px] text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <RowIconAction intent="neutral" label={`Edit ${isAnnual ? 'annual statement' : position.ticker}`} onClick={() => handleEditClick(position)}>
                        <Pencil />
                      </RowIconAction>
                      <RowIconAction intent="negative" label={`Delete ${isAnnual ? 'annual statement' : position.ticker}`} onClick={() => handleDeleteClick(position)}>
                        <Trash2 />
                      </RowIconAction>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <AddClosedPositionDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} onSuccess={handleAddSuccess} />
      <EditClosedPositionDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} onSuccess={handleEditSuccess} position={selectedPosition} />
      <DeleteClosedPositionDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} onSuccess={handleDeleteSuccess} position={selectedPosition} />
    </div>
  );
}

// ============================================
// Dividends Component
// ============================================
function Dividends({ registerToolbarActions }) {
  const [dividends, setDividends] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedDividend, setSelectedDividend] = React.useState(null);

  const fetchDividends = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/transactions/dividends`,
        { withCredentials: true }
      );
      setDividends(response.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching dividends:", err);
      setError(err.message);
    }
  }, []);

  React.useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchDividends();
      setLoading(false);
    };
    loadInitialData();
  }, [fetchDividends]);

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    await fetchDividends();
    setIsRefreshing(false);
  }, [fetchDividends]);

  const handleAddSuccess = () => fetchDividends();

  const handleEditClick = (dividend) => {
    setSelectedDividend(dividend);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    fetchDividends();
    setSelectedDividend(null);
  };

  const handleDeleteClick = (dividend) => {
    setSelectedDividend(dividend);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSuccess = () => {
    fetchDividends();
    setSelectedDividend(null);
  };

  const handleAddDividend = React.useCallback(() => setAddDialogOpen(true), []);

  React.useEffect(() => {
    registerToolbarActions({ onRefresh: handleRefresh, isRefreshing, onAdd: handleAddDividend, addLabel: "Add dividend" });
  }, [registerToolbarActions, handleRefresh, isRefreshing, handleAddDividend]);

  const summary = React.useMemo(() => {
    const totalDividends = dividends.reduce((sum, d) => {
      if (d.statementType === "ANNUAL") {
        return sum + (parseFloat(d.totalDividendAmount) || 0);
      }
      return sum + (parseFloat(d.dividendPerShare) || 0) * (parseFloat(d.sharesOwned) || 0);
    }, 0);

    const individualCount = dividends.filter(d => d.statementType !== "ANNUAL").length;
    const annualCount = dividends.filter(d => d.statementType === "ANNUAL").length;

    return { totalDividends, totalCount: dividends.length, individualCount, annualCount };
  }, [dividends]);

  if (loading) return <LoadingState columns={4} />;

  if (error) {
    return <ErrorCard title="Failed to load dividends" error={error} onRetry={handleRefresh} />;
  }

  if (dividends.length === 0) {
    return (
      <>
        <Card>
          <EmptyState
            icon={TrendingDown}
            title="No Dividends Recorded"
            description="Track your dividend income from stocks, ETFs, and other investments. Add individual dividends or import annual statements from your broker."
            primaryAction={{ label: "Add Dividend", onClick: () => setAddDialogOpen(true) }}
          />
        </Card>
        <AddDividendDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} onSuccess={handleAddSuccess} />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total dividends" value={eur(summary.totalDividends)} valueTone="positive" delta="all time" />
        <StatCard label="Total records" value={String(summary.totalCount)} delta="dividend entries" />
        <StatCard label="Individual" value={String(summary.individualCount)} delta="per-stock dividends" />
        <StatCard label="Annual statements" value={String(summary.annualCount)} delta="broker summaries" />
      </div>

      <Card className="overflow-hidden p-0">
        <TableCardHeader title="Dividend history" count={`${dividends.length} records`} />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-[18px]">Date</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead className="text-right">Shares</TableHead>
              <TableHead className="text-right">Per share</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="px-[18px] text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dividends.slice(0, 15).map((dividend) => {
              const isAnnual = dividend.statementType === "ANNUAL";
              const totalAmount = isAnnual
                ? dividend.totalDividendAmount
                : (dividend.dividendPerShare || 0) * (dividend.sharesOwned || 0);

              return (
                <TableRow key={dividend.transactionId} className={isAnnual ? "bg-accent-tint/40" : undefined}>
                  <TableCell className="px-[18px] font-mono text-[11.5px] text-muted-token">
                    {formatDate(dividend.date || dividend.paymentDate)}
                  </TableCell>
                  <TableCell>
                    {isAnnual ? (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Annual Statement</span>
                        <Badge variant="info" size="sm">Statement</Badge>
                        {dividend.broker && <span className="text-xs text-muted-token">{dividend.broker}</span>}
                      </div>
                    ) : (
                      <span className="whitespace-nowrap">
                        <span className="font-medium">{dividend.assetName || dividend.ticker}</span>
                        {dividend.ticker && dividend.ticker !== dividend.assetName && (
                          <MonoText size="xs" className="ml-1.5 text-text-faint">{dividend.ticker}</MonoText>
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-token">
                    {isAnnual ? "-" : (dividend.sharesOwned || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                  </TableCell>
                  <TableCell className="text-right">
                    {isAnnual ? <span className="font-mono text-text-faint">-</span> : (
                      <CurrencyValue value={parseFloat(dividend.dividendPerShare) || 0} currency={dividend.currency} size="sm" className="font-mono" />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <CurrencyValue value={parseFloat(totalAmount) || 0} currency={dividend.currency} size="sm" colorize className="font-mono font-semibold" />
                  </TableCell>
                  <TableCell className="px-[18px] text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <RowIconAction intent="neutral" label="Edit dividend" onClick={() => handleEditClick(dividend)}>
                        <Pencil />
                      </RowIconAction>
                      <RowIconAction intent="negative" label="Delete dividend" onClick={() => handleDeleteClick(dividend)}>
                        <Trash2 />
                      </RowIconAction>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <AddDividendDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} onSuccess={handleAddSuccess} />
      <EditDividendDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} onSuccess={handleEditSuccess} dividend={selectedDividend} />
      <DeleteDividendDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} onSuccess={handleDeleteSuccess} dividend={selectedDividend} />
    </div>
  );
}

// ============================================
// Withdrawals Component
// ============================================
const REASON_LABELS = {
  living_expenses: "Living Expenses",
  emergency: "Emergency Fund",
  major_purchase: "Major Purchase",
  rebalancing: "Portfolio Rebalancing",
  tax_payment: "Tax Payment",
  other: "Other",
};

function Withdrawals({ registerToolbarActions }) {
  const [withdrawals, setWithdrawals] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = React.useState(null);

  const fetchWithdrawals = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/transactions/withdrawals`,
        { withCredentials: true }
      );
      setWithdrawals(response.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching withdrawals:", err);
      setError(err.message);
    }
  }, []);

  React.useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchWithdrawals();
      setLoading(false);
    };
    loadInitialData();
  }, [fetchWithdrawals]);

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    await fetchWithdrawals();
    setIsRefreshing(false);
  }, [fetchWithdrawals]);

  const handleAddSuccess = () => fetchWithdrawals();

  const handleEditClick = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    fetchWithdrawals();
    setSelectedWithdrawal(null);
  };

  const handleDeleteClick = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSuccess = () => {
    fetchWithdrawals();
    setSelectedWithdrawal(null);
  };

  const handleAddWithdrawal = React.useCallback(() => setAddDialogOpen(true), []);

  React.useEffect(() => {
    registerToolbarActions({ onRefresh: handleRefresh, isRefreshing, onAdd: handleAddWithdrawal, addLabel: "Add withdrawal" });
  }, [registerToolbarActions, handleRefresh, isRefreshing, handleAddWithdrawal]);

  const summary = React.useMemo(() => {
    const totalWithdrawals = withdrawals.reduce((sum, w) => sum + (parseFloat(w.amount) || 0), 0);
    const thisYear = withdrawals
      .filter((w) => new Date(w.date || w.withdrawalDate).getFullYear() === new Date().getFullYear())
      .reduce((sum, w) => sum + (parseFloat(w.amount) || 0), 0);

    return {
      totalWithdrawals,
      totalCount: withdrawals.length,
      average: withdrawals.length > 0 ? totalWithdrawals / withdrawals.length : 0,
      thisYear,
    };
  }, [withdrawals]);

  if (loading) return <LoadingState columns={4} />;

  if (error) {
    return <ErrorCard title="Failed to load withdrawals" error={error} onRetry={handleRefresh} />;
  }

  if (withdrawals.length === 0) {
    return (
      <>
        <Card>
          <EmptyState
            icon={TrendingUp}
            title="No Withdrawals Recorded"
            description="Track cash withdrawals from your investment portfolio. Record withdrawals to monitor your cash-out activity and reasons."
            primaryAction={{ label: "Add Withdrawal", onClick: () => setAddDialogOpen(true) }}
          />
        </Card>
        <AddWithdrawalDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} onSuccess={handleAddSuccess} />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total withdrawals" value={eur(summary.totalWithdrawals)} valueTone="negative" delta="all time" />
        <StatCard label="Total records" value={String(summary.totalCount)} delta="withdrawal entries" />
        <StatCard label="Average withdrawal" value={eur(summary.average)} delta="per transaction" />
        <StatCard label="This year" value={eur(summary.thisYear)} delta={String(new Date().getFullYear())} />
      </div>

      <Card className="overflow-hidden p-0">
        <TableCardHeader title="Withdrawal history" count={`${withdrawals.length} records`} />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-[18px]">Date</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="px-[18px] text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {withdrawals.slice(0, 15).map((withdrawal) => (
              <TableRow key={withdrawal.transactionId}>
                <TableCell className="px-[18px] font-mono text-[11.5px] text-muted-token">
                  {formatDate(withdrawal.date || withdrawal.withdrawalDate)}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" size="sm">
                    {REASON_LABELS[withdrawal.reason] || withdrawal.reason || "Other"}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-token">
                  <span className="block max-w-[220px] truncate" title={withdrawal.notes}>
                    {withdrawal.notes || "-"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <CurrencyValue
                    value={-(parseFloat(withdrawal.amount) || 0)}
                    currency={withdrawal.currency}
                    size="sm"
                    colorize
                    showSign
                    className="font-mono font-semibold"
                  />
                </TableCell>
                <TableCell className="px-[18px] text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <RowIconAction intent="neutral" label="Edit withdrawal" onClick={() => handleEditClick(withdrawal)}>
                      <Pencil />
                    </RowIconAction>
                    <RowIconAction intent="negative" label="Delete withdrawal" onClick={() => handleDeleteClick(withdrawal)}>
                      <Trash2 />
                    </RowIconAction>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <AddWithdrawalDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} onSuccess={handleAddSuccess} />
      <EditWithdrawalDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} onSuccess={handleEditSuccess} withdrawal={selectedWithdrawal} />
      <DeleteWithdrawalDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} onSuccess={handleDeleteSuccess} withdrawal={selectedWithdrawal} />
    </div>
  );
}

// ============================================
// Main Transactions Page
// ============================================
export function Transactions() {
  const { type } = useParams();

  // AppLayout toolbar actions (Refresh + per-view primary action) are driven
  // by whichever sub-view is active, via the same registerToolbarActions
  // bridge pattern established in CashFlow.jsx/Analytics.jsx (#237) — a
  // stable useCallback identity prevents the infinite-render loop those
  // screens' reviews specifically checked for.
  const [toolbarActions, setToolbarActions] = React.useState(null);
  const registerToolbarActions = React.useCallback((actions) => setToolbarActions(actions), []);

  const secondaryActions = toolbarActions ? (
    <Button variant="outline" size="sm" onClick={toolbarActions.onRefresh} disabled={toolbarActions.isRefreshing}>
      <RefreshCw className={`h-3.5 w-3.5 sm:mr-1.5 ${toolbarActions.isRefreshing ? "animate-spin motion-reduce:animate-none" : ""}`} />
      <span className="hidden sm:inline">Refresh</span>
    </Button>
  ) : null;

  const primaryAction = toolbarActions?.onAdd ? (
    <Button size="sm" onClick={toolbarActions.onAdd}>
      <Plus className="h-3.5 w-3.5 mr-1.5" />
      {toolbarActions.addLabel}
    </Button>
  ) : null;

  const renderContent = () => {
    switch (type) {
      case "realized-gains":
        return <RealizedGains key="realized-gains" registerToolbarActions={registerToolbarActions} />;
      case "dividends":
        return <Dividends key="dividends" registerToolbarActions={registerToolbarActions} />;
      case "withdrawals":
        return <Withdrawals key="withdrawals" registerToolbarActions={registerToolbarActions} />;
      default:
        return <ActivityFeed key="activity" registerToolbarActions={registerToolbarActions} />;
    }
  };

  return (
    <AppLayout title="Activity" primaryAction={primaryAction} secondaryActions={secondaryActions}>
      {renderContent()}
    </AppLayout>
  );
}

export default Transactions;
